import { isArray, isDefined, PolygonNode } from "../../infra3d";
import { Geometry, GEOMETRYTYPE, PickData } from "./internal";
/**
 * Representation of the FeatureCollection-Object specified in GeoJSON-Standard.
 *
 * @class MultiPolygonGeometry
 * @author Ported by Benjamin Loesch, based on code by Kevin Flueckiger
 * @extends Geometry
 */
export class MultiPolygonGeometry extends Geometry {
    constructor(parentfeature) {
        super(parentfeature);
        this.nodes = [];
        this.coordinates = [];
        this._coordinates = [];
        this.type = GEOMETRYTYPE.MULTIPOLYGON;
        this.parent = parentfeature;
        this.scene = parentfeature.scene;
    }
    /**
     * Parse the Geometry from a JSON-Object
     * @method parseFromJSON
     * @param {Object} json The JSON Object
     */
    parseFromJSON(json) {
        if (isDefined(json["coordinates"])) {
            this.type = Geometry.toNumber(json["type"]);
            let bError = false;
            for (let j = 0; j < json["coordinates"].length; j++) {
                const ls = json["coordinates"][j];
                for (let i = 0; i < ls.length; i++) {
                    const ps = ls[i];
                    for (let k = 0; k < ps.length; k++) {
                        if (!isArray(ps[k]))
                            bError = true;
                    }
                }
            }
            // If the coordinate-Array is valid, assign it to the Geometries-Coorinate-Property.
            if (bError) {
                throw "Something worng with coordinates array of polygongeometry";
            }
            else {
                for (let j = 0; j < json["coordinates"].length; j++) {
                    const ls = json["coordinates"][j][0];
                    const coords = ls.slice(0);
                    coords.pop();
                    this._coordinates.push(coords);
                    this.coordinates.push(...coords);
                }
                this._create();
            }
        }
    }
    _create() {
        for (const coords of this._coordinates) {
            const node = new PolygonNode(coords, this.parent.style.toMaterialDefinitions(this.parent.properties), this);
            this.scene.add(node);
            this.nodes.push(node);
        }
        this.created = true;
    }
    clear() {
        this.created = false;
        for (const node of this.nodes) {
            this.scene.remove(node);
        }
        this.nodes = [];
        this._coordinates = [];
        this.coordinates = [];
    }
    /**
     * returns the Geometry content as GeoJson string
     *
     * @method toGeoJsonString
     * @return {string}
     */
    toGeoJsonString() {
        const objparts = {};
        objparts["type"] = Geometry.toString(this.type);
        const coords = [];
        for (const parts of this._coordinates) {
            const coords_part = parts.slice(0);
            coords_part.push(coords_part[0]);
            coords.push([coords_part]);
        }
        objparts["coordinates"] = coords;
        return JSON.stringify(objparts);
    }
    /**
     * Removes the Geometry from the Render-Engine and destroys it.
     * @method destroy
     */
    destroy() {
        this.coordinates = [];
        this._coordinates = [];
        this.type = null;
        if (this.created) {
            for (const node of this.nodes) {
                this.scene.remove(node);
                node.destroy();
            }
            this.nodes = [];
        }
        this.id = null;
        this.created = false;
    }
    /**
     * Shows the geometry in Higlight-Style
     * @method highlightOn
     */
    highlightOn() {
        super.highlightOn();
        if (this.created) {
            for (const node of this.nodes) {
                node.setStyle(this.parent.highlightstyle.toMaterialDefinitions(this.parent.properties));
            }
        }
    }
    /**
     * Shows the geometry in Higlight-Style
     * @method highlightOff
     */
    highlightOff() {
        super.highlightOff();
        if (this.created) {
            for (const node of this.nodes) {
                node.setStyle(this.parent.style.toMaterialDefinitions(this.parent.properties));
            }
        }
    }
    /**
     * Updates the Geometry
     * @method update
     */
    update() {
        if (!this.created) {
            this._create();
        }
        else {
            for (const key in this.nodes) {
                const node = this.nodes[key];
                if (!this.parent.highlighton) {
                    node.setStyle(this.parent.style.toMaterialDefinitions(this.parent.properties));
                }
                else {
                    node.setStyle(this.parent.highlightstyle.toMaterialDefinitions(this.parent.properties));
                }
                if (this._coordinates?.[key]) {
                    const coords = this._coordinates[key];
                    node.updateCoordinates(coords);
                }
            }
        }
    }
    /**
     * Shows the Geometry. It sets hide to false and updates the geometry.
     * @method show
     */
    show() {
        super.show();
        if (this.created) {
            for (const node of this.nodes) {
                node.show();
            }
        }
    }
    /**
     * Hides the Geometry. It sets hide to true and updates the geometry.
     * @method hide
     */
    hide() {
        super.hide();
        if (this.created) {
            for (const node of this.nodes) {
                node.hide();
            }
        }
    }
    /**
     * Checks if the node was picked by mouse
     * @method hide
     */
    pick(mx, my, pickeddataarray) {
        if (this.created) {
            for (const node of this.nodes) {
                const p = node.pick(mx, my);
                if (p.length > 0) {
                    const pdata = new PickData();
                    pdata.feature = this.parentfeature;
                    pdata.mindist = p[0].distance;
                    pdata.mx = mx;
                    pdata.my = my;
                    pdata.found = true;
                    pdata.disttopoint = p[0].distance;
                    pdata.found_mx = p[0].point[0];
                    pdata.found_my = p[0].point[1];
                    pdata.geometry = this;
                    pickeddataarray.push(pdata);
                }
            }
        }
    }
}
//# sourceMappingURL=multipolygongeometry.js.map